import { useState, useRef, useEffect, useCallback } from 'react';
import type { MetricExplain } from '../data/metric-explanations.js';

/* ------------------------------------------------------------------ */
/* InfoTooltip — progressive disclosure for metric explanations       */
/* Desktop: hover to show tooltip. Mobile: tap to expand accordion.   */
/* ------------------------------------------------------------------ */

interface InfoTooltipProps {
  explain: MetricExplain;
  compact?: boolean;
}

/**
 * Small "i" icon that shows metric explanation on hover (desktop)
 * or expands an accordion section on tap (mobile).
 */
export function InfoTooltip({ explain, compact }: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isTouchRef = useRef(false);

  // Detect touch devices
  useEffect(() => {
    isTouchRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  // Close on outside click (mobile)
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleToggle = useCallback(() => {
    if (isTouchRef.current) {
      setOpen((prev) => !prev);
    }
  }, []);

  const showTooltip = hovered && !isTouchRef.current;

  return (
    <div className="ti-info-wrapper" ref={wrapperRef}>
      <button
        className="ti-info-btn"
        onClick={handleToggle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label={`More info about ${explain.title}`}
        aria-expanded={open || showTooltip}
        type="button"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
      </button>

      {/* Desktop hover tooltip */}
      {showTooltip && (
        <div className="ti-tooltip" role="tooltip">
          {compact ? (
            <p className="ti-tooltip-text">{explain.what}</p>
          ) : (
            <>
              <p className="ti-tooltip-text"><strong>What it is:</strong> {explain.what}</p>
              <p className="ti-tooltip-text"><strong>Why it matters:</strong> {explain.why}</p>
            </>
          )}
        </div>
      )}

      {/* Mobile tap-to-expand accordion */}
      {open && isTouchRef.current && (
        <div className="ti-info-expanded">
          <div className="ti-info-section">
            <span className="ti-info-label">What it is</span>
            <p>{explain.what}</p>
          </div>
          <div className="ti-info-section">
            <span className="ti-info-label">Why it matters</span>
            <p>{explain.why}</p>
          </div>
          {!compact && (
            <>
              <div className="ti-info-section">
                <span className="ti-info-label">Detection</span>
                <p>{explain.detection}</p>
              </div>
              <div className="ti-info-section">
                <span className="ti-info-label">False positives</span>
                <p>{explain.falsePositives}</p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
